import * as React from 'react';
import {Component, deps, inject, observer} from '../../lib/component';
import {classes, style} from 'typestyle';
import {SVGIcon} from '../svg-icon';
import {betweenJustified, center, centerCenter, horizontal} from 'csstips';
import {important, percent, px} from 'csx';
import {colorBackground, colorSlate, colorSubtle, colorWhite} from '../../theme/color';
import {zAppHeaderContainer} from '../../theme/z';
import {constantDrawerWidth, constantHeaderHeight} from '../../theme/constant';
import {transitionQuickEase} from '../../theme/transition';
import {mediaDesktop, mediaTablet} from '../../theme/media';
import {Badge} from '../badge';
import {Tag} from '../../types/graphql';

interface SearchToolbarProps {
  suggestions: Tag[]
  inputRef: (element: HTMLInputElement) => void
}

@inject(deps) @observer
export class SearchToolbar extends Component<SearchToolbarProps> {
  private $input: HTMLInputElement;

  render() {
    const { showMenuDrawer } = this.props.model.ui;
    const { state, showFilters, filterCount } = this.props.model.page.search;

    const className = classes(
      SearchToolbar.styles.container,
      showMenuDrawer && SearchToolbar.styles.containerShunted
    );

    return (
      <div className={className}>
        <form className={SearchToolbar.styles.searchBar} onSubmit={this.handleSubmit}>
          <SVGIcon.Search
            className={SearchToolbar.styles.searchIcon}
            size={18}
            color={colorSubtle.toString()}
          />
          <input
            className={SearchToolbar.styles.input}
            value={state.query || ''}
            onChange={this.handleChangeQuery}
            placeholder="Search all tracks..."
            autoFocus={true}
            ref={this.bindInputRef}
          />
          <div data-role="button" className={classes(SearchToolbar.styles.filterButton, showFilters && SearchToolbar.styles.filterButtonActive)} onClick={this.handleClickFilter}>
            <SVGIcon.Filters className={SearchToolbar.styles.filtersIcon} color={colorSlate.toString()} />
            {filterCount > 0 && (
              <Badge className={SearchToolbar.styles.badge}>
                {filterCount}
              </Badge>
            )}
          </div>
        </form>
      </div>
    )
  }

  bindInputRef = (element: HTMLInputElement) => {
    this.$input = element;
    this.props.inputRef(element);
  };

  handleChangeQuery = (event: React.FormEvent<HTMLInputElement>) => {
    this.props.controller.page.search.refineQuery(event.currentTarget.value);
  };

  handleClickFilter = (event: React.MouseEvent<any>) => {
    this.props.controller.page.search.toggleFilters();
  };

  handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (this.$input) {
      this.$input.blur();
    }
  };

  componentDidMount() {
    if (this.$input) {
      this.$input.focus();
      this.$input.selectionStart = this.$input.selectionEnd = this.$input.value.length;
    }
  };

  static styles = {
    container: style(
      {
        position: 'fixed',
        zIndex: zAppHeaderContainer - 1,
        top: 0,
        left: 0,
        right: 0,
        margin: `${constantHeaderHeight} 0 0 0`,
        transition: `top ${transitionQuickEase}`,
        willChange: 'top',
        background: colorBackground.toString()
      },
    ),
    containerShunted: style(
      mediaDesktop({
        right: constantDrawerWidth
      })
    ),
    searchBar: style({
      ...horizontal,
      ...betweenJustified,
      ...center,
      margin: 0,
      padding: 0,
      border: 'none'
    }),
    input: style({
      margin: 0,
      flex: 1,
      padding: '0 16px 0 62px',
      lineHeight: constantHeaderHeight,
      fontSize: px(20),
      background: 'transparent',
      border: 0,
    }),
    searchIcon: style({
      position: important('absolute'),
      left: px(22),
      top: percent(50),
      marginTop: px(-10),
    }),
    filterButton: style(
      {
        ...centerCenter,
        cursor: 'pointer',
        position: 'relative',
        background: colorBackground.toString(),
        transition: `background ${transitionQuickEase}`,
        width: constantHeaderHeight,
        height: constantHeaderHeight,
      },
      mediaTablet({
        display: 'none'
      })
    ),
    filtersIcon: style({
      transform: 'rotate(90deg)'
    }),
    filterButtonActive: style({
      background: colorBackground.lighten(0.05).toString()
    }),
    badge: style({
      backgroundColor: important(colorSlate.toString()),
      color: colorWhite.toString(),
      position: 'absolute',
      top: px(10),
      left: px(30)
    })
  }
}